/**
 * FERP Portal - Alert List Page
 *
 * LIFE-CRITICAL: Alerts are the primary mechanism for surfacing
 * patient safety issues, financial anomalies, and compliance violations.
 *
 * NOTE: This page uses useListSafetyEventsQuery since useListAlertsQuery
 * is not yet available in the backend schema.
 */

import { useMemo } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Button,
  Table,
  Alert as MantineAlert,
  Loader,
  Paper,
  ThemeIcon,
} from '@mantine/core';
import {
  IconBell,
  IconAlertTriangle,
  IconAlertCircle,
  IconInfoCircle,
  IconBellRinging,
  IconRefresh,
} from '@tabler/icons-react';
import { useListSafetyEventsQuery, type SafetyEventFilter } from '@/generated/graphql';
import { useTenantId } from '@/auth';
import {
  ALERT_SEVERITY_UI_CONFIG as SEVERITY_CONFIG,
  type AlertSeverity as SeverityType,
} from '@/config/alert-severity';

export function AlertListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  // Use SafetyEvents as the closest available query
  const filter: SafetyEventFilter = {
    tenantId: tenantId,
  };

  const { data, loading, error, refetch } = useListSafetyEventsQuery({
    variables: { filter },
    skip,
  });

  // Map safety events to alert-like format
  const alerts = useMemo(() => {
    const events = data?.listSafetyEvents ?? [];
    return events.map((event) => ({
      id: event.eventId,
      severity: event.severity as SeverityType,
      category: event.category,
      description: event.description,
      status: event.remediationStatus,
      remediationRequired: event.remediationRequired,
      createdAt: event.createdAt,
    }));
  }, [data]);

  // Compute stats
  const stats = useMemo(() => {
    const total = alerts.length;
    const critical = alerts.filter((a) => a.severity === 'CRITICAL').length;
    const requiresRemediation = alerts.filter((a) => a.remediationRequired).length;
    return { total, critical, requiresRemediation };
  }, [alerts]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <MantineAlert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view alerts.
        </MantineAlert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <MantineAlert icon={<IconAlertCircle size={16} />} color="red" title="Error Loading Alerts">
          {error.message}
        </MantineAlert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{translate('alert')}s</Title>
            <Text c="dimmed" size="sm">
              Monitor and respond to {translate('alert').toLowerCase()}s
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Critical Alert Banner */}
        {stats.critical > 0 && (
          <MantineAlert
            icon={<IconBellRinging size={16} />}
            color="red"
            title="Critical Safety Events"
          >
            {stats.critical} critical event{stats.critical !== 1 ? 's require' : ' requires'} immediate attention.
          </MantineAlert>
        )}

        {/* Stats Cards */}
        <Group gap="md">
          <Card withBorder padding="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconBell size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Events</Text>
                <Text size="xl" fw={700}>{stats.total}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder padding="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="red">
                <IconBellRinging size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Critical</Text>
                <Text size="xl" fw={700} c={stats.critical > 0 ? 'red' : undefined}>
                  {stats.critical}
                </Text>
              </div>
            </Group>
          </Card>
          <Card withBorder padding="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="orange">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Requires Remediation</Text>
                <Text size="xl" fw={700}>{stats.requiresRemediation}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Events Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Severity</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {alerts.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="md">
                        No safety events found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  alerts.slice(0, 20).map((alert) => {
                    const severityConfig = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.INFO;
                    const SeverityIcon = severityConfig.icon;
                    return (
                      <Table.Tr key={alert.id}>
                        <Table.Td>
                          <Badge
                            color={severityConfig.color}
                            leftSection={<SeverityIcon size={12} />}
                            size="sm"
                          >
                            {severityConfig.label}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{alert.category}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>{alert.description}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="light"
                            color={alert.remediationRequired ? 'orange' : 'green'}
                          >
                            {alert.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {new Date(alert.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default AlertListPage;
